import React, { useCallback, useEffect, useState } from 'react';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import { Field, Input, Select } from '../../ui/Field/Field';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table';
import { finesService, errorMessage } from '../../services/finesService';
import MailSenderCard from './MailSenderCard';

/**
 * Administration > E-mail report (owner, 5 Oct 2026: "انا عايز كل ده يكون فى اعدادات الادمن يغير الاميلات ويضيف اميلات
 * جديدة وهكذا"): who gets the daily rental report e-mail - To and CC - added, edited, moved between To and CC or
 * removed; each change is saved on the server at once (the WHOLE list). The sender is always the server's account
 * ("المرسل نفس الاميل"); the time is set in Schedules. The engine checks every address and lets the admin only.
 */
const EMAIL = /^[^@\s,;<>]+@[^@\s,;<>]+\.[^@\s,;<>]+$/;
const rowsOf = (s) => [...(s?.to || []).map((email) => ({ email, kind: 'To' })), ...(s?.cc || []).map((email) => ({ email, kind: 'CC' }))];

const DailyEmailPanel = () => {
    const [state, setState] = useState(null);          // {to, cc, from, time, missing, by, at}
    const [loadError, setLoadError] = useState('');
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);
    const [email, setEmail] = useState('');
    const [kind, setKind] = useState('To');
    const [editing, setEditing] = useState(null);      // {from: old address, value}

    const load = useCallback(async () => {
        try { setState(await finesService.dailyEmailRecipientsGet()); setLoadError(''); } catch (e) { setLoadError(errorMessage(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const ready = !!state && !loadError;
    const rows = rowsOf(state);
    // a save is the whole list: never built on a list that could not be read
    const save = async (next, done) => {
        if (!ready) return;
        if (!next.some((r) => r.kind === 'To')) { setNotice('Keep at least one address in To.'); return; }
        setBusy(true); setNotice(null);
        try {
            const saved = await finesService.dailyEmailRecipients(next.filter((r) => r.kind === 'To').map((r) => r.email),
                next.filter((r) => r.kind === 'CC').map((r) => r.email));
            setState((s) => ({ ...s, ...saved }));
            setNotice(done);
            setEditing(null);
            load();
        } catch (e) { setNotice(errorMessage(e)); } finally { setBusy(false); }
    };
    const has = (address, except) => rows.some((r) => r.email.toLowerCase() === address.toLowerCase() && r.email !== except);
    const add = () => {
        const a = email.trim();
        if (!EMAIL.test(a)) { setNotice(`"${a}" is not an e-mail address`); return; }
        if (has(a)) { setNotice(`${a} is on the list already`); return; }
        setEmail('');
        save([...rows, { email: a, kind }], `${a} added (${kind})`);
    };
    const saveEdit = () => {
        const a = editing.value.trim();
        if (!EMAIL.test(a)) { setNotice(`"${a}" is not an e-mail address`); return; }
        if (has(a, editing.from)) { setNotice(`${a} is on the list already`); return; }
        save(rows.map((r) => (r.email === editing.from ? { ...r, email: a } : r)), `${editing.from} changed to ${a}`);
    };
    const move = (r) => {
        const to = r.kind === 'To' ? 'CC' : 'To';
        save(rows.map((x) => (x.email === r.email ? { ...x, kind: to } : x)), `${r.email} moved to ${to}`);
    };
    const remove = (r) => save(rows.filter((x) => x.email !== r.email), `${r.email} removed`);

    return (
        <div className="flex flex-col gap-4">
            <MailSenderCard onChanged={load} />
            <Card title="Daily report e-mail"
                subtitle="Yesterday's daily rental report is e-mailed every night. Add the people who get it - To or CC - change or remove them; each change is saved at once.">
                <div className="grid gap-x-4 gap-y-1 text-sm mb-4" style={{ gridTemplateColumns: 'max-content 1fr' }}>
                    <span className="font-semibold text-gray-700">From</span>
                    {/* the Gmail box already kept for the RTA code sends it (owner, 5 Oct 2026: "مش عايز نكرر حجات هى معموله اصلا") */}
                    <span className="text-gray-900">
                        {state?.from
                            ? `${state.from}${{ resend: ' · yelodubai.com (the Sender above)', 'rta-mailbox': ' · the Gmail box kept for the RTA code (The Engine > Salik)' }[state.fromSource] || ' · set on the server'}`
                            : '— no Gmail box yet: keep the RTA code mailbox (Gmail + App Password) in The Engine > Salik'}
                    </span>
                    <span className="font-semibold text-gray-700">Sent at</span>
                    <span className="text-gray-900">{state?.time || '—'} every night (Dubai) · change it in Schedules</span>
                    <span className="font-semibold text-gray-700">State</span>
                    <span className={state?.missing ? 'text-amber-800 font-medium' : 'text-green-700 font-medium'}>
                        {!state ? '—' : state.missing ? `Not sent yet: ${state.missing}` : 'Ready - it goes out tonight'}
                    </span>
                    {state?.by && (<>
                        <span className="font-semibold text-gray-700">Last change</span>
                        <span className="text-gray-900">{state.by} · {String(state.at).replace('T', ' ').slice(0, 16)}</span>
                    </>)}
                </div>
                <div className="flex flex-wrap items-end gap-3">
                    <Field label="E-mail address" className="flex-1 min-w-[240px]">
                        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()}
                            placeholder="name@company.com" className="w-full" aria-label="New e-mail address" />
                    </Field>
                    <Field label="As">
                        <Select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="To or CC">
                            <option value="To">To</option>
                            <option value="CC">CC</option>
                        </Select>
                    </Field>
                    <Button variant="primary" onClick={add} disabled={busy || !ready || !email.trim()} isLoading={busy}
                        leadingIcon={<Icon glyph={ICONS.plus} size="sm" />}>Add</Button>
                </div>
                {loadError && (
                    <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
                        <span className="flex-1">The list could not be read: {loadError}. Changes wait until it is read.</span>
                        <Button size="sm" variant="secondary" onClick={load} disabled={busy}>Try again</Button>
                    </div>
                )}
                {notice && <div className="mt-4 rounded-lg bg-gray-100 px-4 py-3 text-sm text-gray-800" role="status" aria-live="polite">{notice}</div>}
            </Card>
            <Card title={`Recipients · ${rows.length}`}>
                {rows.length === 0 ? (
                    <div className="text-sm text-gray-600">{state ? 'No one yet - add the first address above.' : 'Loading…'}</div>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead><TableRow>
                                <TableHeader>#</TableHeader><TableHeader>E-mail</TableHeader><TableHeader>As</TableHeader><TableHeader>Actions</TableHeader>
                            </TableRow></TableHead>
                            <TableBody>
                                {rows.map((r, i) => (
                                    <TableRow key={r.email}>
                                        <TableCell>{i + 1}</TableCell>
                                        <TableCell>
                                            {editing?.from === r.email ? (
                                                <Input size="sm" value={editing.value} aria-label={`Edit ${r.email}`} autoFocus
                                                    onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(null); }} />
                                            ) : r.email}
                                        </TableCell>
                                        <TableCell><span className={r.kind === 'To' ? 'font-semibold text-purple-800' : 'text-gray-700'}>{r.kind}</span></TableCell>
                                        <TableCell>
                                            <div className="flex flex-wrap justify-center gap-1">
                                                {editing?.from === r.email ? (<>
                                                    <Button size="sm" variant="primary" disabled={busy} onClick={saveEdit}>Save</Button>
                                                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEditing(null)}>Cancel</Button>
                                                </>) : (<>
                                                    <Button size="sm" variant="ghost" disabled={busy || !ready} onClick={() => setEditing({ from: r.email, value: r.email })}
                                                        aria-label={`Edit ${r.email}`}>Edit</Button>
                                                    <Button size="sm" variant="ghost" disabled={busy || !ready} onClick={() => move(r)}
                                                        aria-label={`Move ${r.email} to ${r.kind === 'To' ? 'CC' : 'To'}`}>{r.kind === 'To' ? 'Move to CC' : 'Move to To'}</Button>
                                                    <Button size="sm" variant="ghost" disabled={busy || !ready} onClick={() => remove(r)}
                                                        leadingIcon={<Icon glyph={ICONS.close} size="sm" />} aria-label={`Remove ${r.email}`}>Remove</Button>
                                                </>)}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Card>
        </div>
    );
};

export default DailyEmailPanel;
